import { catchError, finalize, tap } from 'rxjs/operators';
import DepartmentGroupService from 'src/app/shared/services/department-group.service';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { of, Subscription } from 'rxjs';
import { SnackbarService } from 'ok-angular-shared/components';
import { TopLoaderService } from 'src/app/core/services/top-loader.service';
import { HttpErrorResponse } from '@angular/common/http';
import { DepartmentGroup, CreateDepartmentGroupRequest, UpdateDepartmentGroupRequest } from '../../departmentGroup.model';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'adm-department-group-input',
  templateUrl: './department-group-input.component.html',
  styleUrls: ['./department-group-input.component.scss'],
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule],
})
export class DepartmentGroupInputComponent implements OnInit, OnChanges, OnDestroy {
  private snackbarService = inject(SnackbarService);
  private fb = inject(FormBuilder);
  private departmentGroupService = inject(DepartmentGroupService);
  private loader = inject(TopLoaderService);

  @Input() departmentGroups: DepartmentGroup[];
  @Input() departmentGroupToEdit: DepartmentGroup | null;
  @Input() organizationId: number;

  @Output() editMode: boolean;
  @Output() done = new EventEmitter<DepartmentGroup>();

  form: FormGroup;

  private subscriptions = new Subscription();

  get name(): FormControl<string | null> {
    return this.form.get('name') as FormControl<string | null>;
  }

  ngOnInit() {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50), Validators.pattern(/.*\S.*/)]],
    });
  }

  ngOnChanges(): void {
    if (this.departmentGroupToEdit) {
      this.editMode = true;
      this.initializeEdit();
    } else {
      this.editMode = false;
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  private initializeEdit() {
    if (!this.departmentGroupToEdit) {
      return;
    }

    this.form.markAllAsTouched();

    this.name.setValue(this.departmentGroupToEdit.name);
  }

  submit() {
    if (this.form.valid) {
      const departmentGroupToEdit = this.departmentGroupToEdit;

      if (!departmentGroupToEdit?.id) {
        const createObject: CreateDepartmentGroupRequest = {
          name: this.name.value ?? '',
          organizationId: this.organizationId,
        };
        this.loader.setLoading(true);
        this.subscriptions.add(
          this.departmentGroupService
            .createDepartmentGroup(createObject)
            .pipe(
              catchError((httpError: HttpErrorResponse) => {
                //TODO: log error when able to

                if (httpError.status == 400 && httpError.error) {
                  const error = httpError.error as ErrorMessage;
                  if (error.code === 100166) {
                    this.snackbarService.warning(`Kan ikke oprette afdelingsgruppe da det angivne navn allerede er i brug.`);
                    return of();
                  }
                }
                this.snackbarService.error('Der skete en fejl under oprettelse af afdelingsgruppe.');
                return of();
              }),
              finalize(() => this.loader.setLoading(false))
            )
            .subscribe(() => {
              this.clearForm();
              this.done.emit();
            })
        );
      } else {
        const editObject: UpdateDepartmentGroupRequest = {
          name: this.name.value ?? '',
        };
        this.loader.setLoading(true);

        this.subscriptions.add(
          this.departmentGroupService
            .updateDepartmentGroup(departmentGroupToEdit.id, editObject)
            .pipe(
              catchError((httpError: HttpErrorResponse) => {
                //TODO: log error when able to

                if (httpError.status == 404 && httpError.error) {
                  const error = httpError.error as ErrorMessage;
                  if (error.code === 100160) {
                    this.snackbarService.warning(`Afdelingsgruppen kunne ikke findes.`);
                    return of();
                  }
                } else if (httpError.status == 400 && httpError.error) {
                  const error = httpError.error as ErrorMessage;
                  if (error.code === 100166) {
                    this.snackbarService.warning(`Gruppenavnet er allerede i brug.`);
                    return of();
                  }
                }
                this.snackbarService.error('Der skete en fejl under opdatering af afdelingsgruppe.');
                return of();
              }),
              tap(() => (editObject.name = this.name.value ?? '')),
              finalize(() => this.loader.setLoading(false))
            )
            .subscribe(() => {
              const resultObject: DepartmentGroup = {
                id: departmentGroupToEdit.id,
                name: editObject.name,
              };
              this.done.emit(resultObject);
              this.clearForm();
            })
        );
      }
    }
  }

  private clearForm() {
    this.form.reset();
    this.form.clearValidators();
  }
}
